/**
 * Gestion des pistes dans le HUD :
 * - carte « Tracer une piste » du menu de construction ;
 * - barre de l'outil de tracé (piste neuve) et de reprise d'un tracé, avec le
 *   verdict de l'aperçu (difficulté, longueur, coût, raccordements) ;
 * - panneau « Pistes » (touche P) : où chaque niveau peut aller depuis le
 *   village, défauts du réseau, liste des pistes ;
 * - actions d'une piste dans sa fiche : raccords, reprise, nom, démolition.
 */
import { useState } from 'preact/hooks';
import { Check, MountainSnow, Pencil, Plus, Route, Tag, Trash2, TriangleAlert, Undo2, X } from 'lucide-preact';
import {
  DIFFICULTY_LABEL,
  DIFFICULTY_RANK,
  ECONOMY,
  PISTE_ROUTES,
  PISTE_ROUTE_LABEL,
  PISTE_SIZE_LABEL,
  SKILL_LEVELS,
  forestShare,
  isValidPisteName,
  parsePisteNode,
  pisteCost,
  type NetworkIssue,
  type Piste,
  type PisteNetwork,
  type PisteRoute,
  type PisteSize,
} from '@snowchain/shared';
import { Button, HudPanel } from './components';
import { command, focusEntity, hud } from './context';
import { euros, n0, n1 } from './format';
import { DIFF_COLOR } from './labels';
import { Kbd, Medal } from './game-ui';
import { cancelDemolish, demolishPiste, demolishWait, pisteFacts, pisteView, startEdit, startTrace, stopEdit, submitPiste } from './piste-state';
import { ui } from './ui-state';
import './pistes.css';

const SIZES: PisteSize[] = ['narrow', 'normal', 'wide'];

const ROUTE_HINT: Record<PisteRoute, string> = {
  direct: 'Passe au plus près de chaque point, sans lacets',
  verte: 'Lacets pour tenir une pente de verte entre les points',
  bleue: 'Lacets pour tenir une pente de bleue entre les points',
  rouge: 'Lacets pour tenir une pente de rouge entre les points',
  noire: 'Descend au plus droit, pente de noire',
};

const km = (m: number) => `${n1(m / 1000)} km`;

/** Carte « Tracer une piste » du menu de construction. */
export function PisteBuildCard() {
  return (
    <button class="kh-card" onClick={startTrace}>
      <span class="kh-card-title">
        <Route size={14} /> Tracer une piste
      </span>
      <span class="kh-card-cost">À partir de {euros(pisteCost(200, 'normal'))}</span>
      <span class="kh-card-desc">Départ au sommet d'une remontée, à une gare ou sur une piste ; arrivée à une gare, sur une piste ou au village.</span>
    </button>
  );
}

/** Barre de l'outil pendant le tracé d'une piste neuve (affichée par le menu de construction). */
export function PisteBuildBar() {
  return <ToolBar mode="trace" />;
}

function ToolBar(props: { mode: 'trace' | 'edit' }) {
  void ui.tick.value;
  const v = pisteView.value;
  const b = ui.build.value;
  const e = ui.pisteEdit.value;
  const trace = props.mode === 'trace' && b?.kind === 'piste' ? b : null;
  const edit = props.mode === 'edit' ? e : null;
  if (!trace && !edit) return null;
  const route = (trace ?? edit)!.route;
  const size = (trace ?? edit)!.size;
  const count = (trace ?? edit)!.points.length;
  const piste = edit ? hud.store?.replica?.pistes.get(edit.id) : undefined;
  const set = (patch: { route?: PisteRoute; size?: PisteSize }) => {
    if (trace) ui.build.value = { ...trace, ...patch };
    else if (edit) ui.pisteEdit.value = { ...edit, ...patch };
  };
  const ready = !!v?.ok && count >= 2 && !edit?.waiting;
  let verdict;
  if (edit?.waiting) {
    verdict = <span class="pst-wait">Fermeture : reprise dès que les skieurs auront quitté la piste ({n0(piste?.traffic ?? 0)} encore dessus)</span>;
  } else if (!v || (trace && count === 0 && !v.from)) {
    verdict = <span class="muted">{v?.hint ?? 'Survolez le terrain'}</span>;
  } else if (v.ok) {
    verdict = (
      <span class="kh-valid">
        <i class="pst-dot" style={{ background: DIFF_COLOR[v.difficulty ?? 'bleue'] }} />
        {DIFFICULTY_LABEL[v.difficulty ?? 'bleue']} · {n0(v.length ?? 0)} m · {n0(v.drop ?? 0)} m de dénivelé · {euros(v.cost)} · {n1(v.hours ?? 0)} h de travaux
      </span>
    );
  } else if (v.pending) {
    verdict = (
      <span class="pst-pending">
        <i class="pst-dot" style={{ background: DIFF_COLOR[v.difficulty ?? 'bleue'] }} />
        {DIFFICULTY_LABEL[v.difficulty ?? 'bleue']} · {n0(v.length ?? 0)} m · {v.reason}
      </span>
    );
  } else if (count === 0 || (trace && count === 1 && !v.reason)) {
    verdict = <span class="kh-valid">{v.hint}</span>;
  } else {
    verdict = (
      <span class="kh-invalid">
        {v.reason}
        {v.cost > 0 ? ` · ${euros(v.cost)}` : ''}
      </span>
    );
  }
  return (
    <HudPanel class="kh-build kh-build-compact pst-bar">
      <div class="pst-bar-row">
        <span class="g-place-ic" style={{ '--cat': 'var(--diff-bleue)' } as Record<string, string>}>
          <Route size={16} />
        </span>
        <span class="pst-bar-title">{trace ? 'Nouvelle piste' : `Reprise du tracé · ${piste?.name ?? ''}`}</span>
        <span class="pst-bar-verdict">{verdict}</span>
        <span class="pst-bar-actions">
          {trace && count > 0 && (
            <Button size="sm" variant="ghost" title="Retirer le dernier point (Retour arrière)" onClick={() => (ui.build.value = { ...trace, points: trace.points.slice(0, -1) })}>
              <Undo2 size={14} /> Point
            </Button>
          )}
          <Button size="sm" disabled={!ready} onClick={() => void submitPiste()} title="Valider (Entrée)">
            <Check size={14} /> {trace ? 'Construire' : 'Valider'}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => (trace ? (ui.build.value = null) : stopEdit(false))}>
            {trace ? 'Changer' : 'Annuler'}
          </Button>
        </span>
      </div>
      {v && (v.from || v.to) && (
        <div class="pst-bar-row pst-bar-ends">
          <span>
            <b>Départ</b> {v.from ?? 'non raccordé'}
          </span>
          {count + (trace && v.to ? 1 : 0) >= 2 && (
            <span>
              <b>Arrivée</b> {v.to ?? 'non raccordée'}
            </span>
          )}
          {trace && count > 0 && v.hint && <span class="muted">Ici : {v.hint}</span>}
        </div>
      )}
      <div class="pst-bar-row">
        <span class="microlabel">Tracé</span>
        <span class="kh-seg">
          {PISTE_ROUTES.map((r) => (
            <button key={r} class={route === r ? 'on' : ''} title={ROUTE_HINT[r]} onClick={() => set({ route: r })}>
              {PISTE_ROUTE_LABEL[r]}
            </button>
          ))}
        </span>
        <span class="microlabel">Largeur</span>
        <span class="kh-seg">
          {SIZES.map((s) => (
            <button key={s} class={size === s ? 'on' : ''} onClick={() => set({ size: s })}>
              {PISTE_SIZE_LABEL[s]}
            </button>
          ))}
        </span>
        <span class="pst-keys muted">
          {trace ? (
            <>
              <Kbd>Clic</Kbd> point <Kbd>Double-clic</Kbd> ou <Kbd>Entrée</Kbd> construire <Kbd>Retour</Kbd> retirer <Kbd>Échap</Kbd> quitter
            </>
          ) : (
            <>
              <Kbd>Glisser</Kbd> déplacer <Kbd>Clic</Kbd> sur le tracé : ajouter <Kbd>Clic droit</Kbd> retirer <Kbd>Entrée</Kbd> valider <Kbd>Échap</Kbd> annuler
            </>
          )}
        </span>
      </div>
    </HudPanel>
  );
}

/** Pistes raccordées à celle-ci : celles qui en partent (bifurcations) et celles qui y arrivent. */
function junctions(net: PisteNetwork, id: number): { branches: { id: number; name: string; s: number }[]; merges: { id: number; name: string; s: number }[] } {
  const branches: { id: number; name: string; s: number }[] = [];
  const merges: { id: number; name: string; s: number }[] = [];
  for (const info of net.pistes.values()) {
    if (info.piste.id === id) continue;
    const f = parsePisteNode(info.from);
    if (f?.piste === id) branches.push({ id: info.piste.id, name: info.piste.name, s: f.s });
    const t = parsePisteNode(info.to);
    if (t?.piste === id) merges.push({ id: info.piste.id, name: info.piste.name, s: t.s });
  }
  branches.sort((a, b) => a.s - b.s);
  merges.sort((a, b) => a.s - b.s);
  return { branches, merges };
}

function IssueLine(props: { issue: NetworkIssue; onClick?: () => void }) {
  const i = props.issue;
  return (
    <button type="button" class={`pst-issue pst-issue-${i.level}`} onClick={props.onClick}>
      <TriangleAlert size={13} />
      <span>
        <b>{i.title}</b>
        <span class="muted"> · {i.message}</span>
      </span>
    </button>
  );
}

/** Actions et raccords d'une piste, dans sa fiche (inspecteur). */
export function PisteManage(props: { piste: Piste }) {
  const p = props.piste;
  const store = hud.store;
  const net = store?.network();
  const [renaming, setRenaming] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  if (!store || !net) return null;
  const owner = store.isOwner;
  const { branches, merges } = junctions(net, p.id);
  const info = net.pistes.get(p.id);
  // Remontées posées le long de la piste, dans l'ordre de la descente.
  const stations = info ? [...info.entries, ...info.stops].sort((a, b) => a.s - b.s) : [];
  const issues = net.issues().filter((i) => i.pisteId === p.id);
  const waiting = demolishWait.value === p.id;
  const refund = p.waypoints.length >= 2 ? Math.round(pisteCost(p.length, p.size, forestShare(store.hf, store.map, p.path)) * ECONOMY.demolishRefund) : 0;
  const rename = async () => {
    const name = (renaming ?? '').trim();
    if (!isValidPisteName(name)) return;
    if (await command({ type: 'renamePiste', id: p.id, name }, 'Piste renommée')) setRenaming(null);
  };
  return (
    <div class="pst-manage">
      {p.progress < 1 && (
        <div class="pst-works">
          <span>Travaux {n0(p.progress * 100)} %</span>
          <span class="pst-meter">
            <i style={{ width: `${Math.round(p.progress * 100)}%` }} />
          </span>
        </div>
      )}
      <div class="pst-ends">
        <span>
          <b>Départ</b> {net.label(p.from)}
        </span>
        <span>
          <b>Arrivée</b> {net.label(p.to)}
        </span>
      </div>
      {branches.length > 0 && (
        <p class="g-sub pst-joins">
          Bifurcation{branches.length > 1 ? 's' : ''} :{' '}
          {branches.map((j, k) => (
            <span key={j.id}>
              {k > 0 && ', '}
              <button type="button" class="g-link pst-link" onClick={() => focusEntity('piste', j.id)}>
                {j.name}
              </button>{' '}
              à {n0(j.s)}{'\u00a0'}m
            </span>
          ))}
        </p>
      )}
      {merges.length > 0 && (
        <p class="g-sub pst-joins">
          La rejoi{merges.length > 1 ? 'gnent' : 'nt'} :{' '}
          {merges.map((j, k) => (
            <span key={j.id}>
              {k > 0 && ', '}
              <button type="button" class="g-link pst-link" onClick={() => focusEntity('piste', j.id)}>
                {j.name}
              </button>{' '}
              à {n0(j.s)}{'\u00a0'}m
            </span>
          ))}
        </p>
      )}
      {stations.map((st) => (
        <p class="g-sub pst-joins" key={`${st.node}`}>
          {st.node[0] === 'B' ? 'Gare aval en chemin : ' : 'Accès depuis le sommet : '}
          <button type="button" class="g-link pst-link" onClick={() => focusEntity('lift', st.liftId)}>
            {store.replica?.lifts.get(st.liftId)?.name ?? 'remontée'}
          </button>{' '}
          à {n0(st.s)}{'\u00a0'}m
        </p>
      ))}
      {issues.map((i) => (
        <IssueLine key={i.kind} issue={i} />
      ))}
      {owner && renaming !== null && (
        <form
          class="pst-rename"
          onSubmit={(ev) => {
            ev.preventDefault();
            void rename();
          }}
        >
          <input
            class="pst-input"
            value={renaming}
            maxLength={24}
            autoFocus
            aria-label="Nouveau nom de la piste"
            onInput={(ev) => setRenaming((ev.target as HTMLInputElement).value)}
            onKeyDown={(ev) => {
              if (ev.key === 'Escape') {
                ev.stopPropagation();
                setRenaming(null);
              }
            }}
          />
          <Button size="sm" type="submit" disabled={!isValidPisteName(renaming.trim())}>
            <Check size={14} /> Renommer
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setRenaming(null)}>
            Annuler
          </Button>
        </form>
      )}
      {owner && confirm && !waiting && (
        <div class="pst-confirm">
          <p>
            Démolir <b>{p.name}</b> ?{' '}
            {branches.length + merges.length > 0
              ? `${[...branches, ...merges].map((j) => j.name).join(', ')} ne ser${branches.length + merges.length > 1 ? 'ont' : 'a'} plus raccordée${branches.length + merges.length > 1 ? 's' : ''}. `
              : ''}
            {refund > 0 ? `Remboursement : ${euros(refund)}.` : 'Sans remboursement : la piste est comprise dans le prix de sa remontée.'}
          </p>
          <div class="kh-actions">
            <Button
              size="sm"
              variant="danger"
              onClick={() => {
                setConfirm(false);
                void demolishPiste(p.id);
              }}
            >
              <Trash2 size={14} /> Démolir
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirm(false)}>
              Garder
            </Button>
          </div>
        </div>
      )}
      {owner && waiting && (
        <div class="pst-confirm">
          <p>Démolition dès que les skieurs auront quitté la piste ({n0(p.traffic)} encore dessus).</p>
          <Button size="sm" variant="ghost" onClick={cancelDemolish}>
            Annuler la démolition
          </Button>
        </div>
      )}
      {owner && renaming === null && !confirm && !waiting && (
        <div class="kh-actions">
          <Button size="sm" variant="secondary" onClick={() => startEdit(p)}>
            <Pencil size={14} /> Modifier le tracé
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setRenaming(p.name)}>
            <Tag size={14} /> Renommer
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setConfirm(true)}>
            <Trash2 size={14} /> Démolir
          </Button>
        </div>
      )}
    </div>
  );
}

function stateText(p: Piste): string {
  if (p.progress < 1) return `Travaux ${n0(p.progress * 100)} %`;
  if (!p.open) return p.closedReason ?? 'Fermée';
  return p.traffic > 0 ? `${n0(p.traffic)} skieur${p.traffic > 1 ? 's' : ''}` : 'Ouverte';
}

/** Panneau « Pistes » (touche P), et barre de reprise d'un tracé. */
export function PistesPanel() {
  return (
    <>
      {ui.pisteEdit.value && <ToolBar mode="edit" />}
      {ui.pistesPanel.value && <PistesSheet />}
    </>
  );
}

function PistesSheet() {
  void ui.tick.value;
  const store = hud.store;
  const r = store?.replica;
  const net = store?.network();
  if (!store || !r || !net) return null;
  const owner = store.isOwner;
  const pistes = [...r.pistes.values()].sort((a, b) => DIFFICULTY_RANK[a.difficulty] - DIFFICULTY_RANK[b.difficulty] || a.name.localeCompare(b.name, 'fr'));
  const open = pistes.filter((p) => p.open);
  const openM = open.reduce((s, p) => s + p.length, 0);
  const totalM = pistes.reduce((s, p) => s + p.length, 0);
  const levels = SKILL_LEVELS.map((lv) => {
    const reach = net.reach('V', { skill: lv.skill, openOnly: true });
    let m = 0;
    for (const [id, s0] of reach.pistes) {
      const info = net.pistes.get(id);
      if (info) m += Math.max(0, info.length - s0);
    }
    return { lv, n: reach.pistes.size, m, lifts: reach.lifts.size };
  });
  const issues = net.issues();
  const focusIssue = (i: NetworkIssue) => {
    if (i.pisteId) focusEntity('piste', i.pisteId);
    else if (i.liftId) focusEntity('lift', i.liftId);
  };
  return (
    <section class="sc-hud pst-panel" aria-label="Pistes">
      <div class="g-card-head">
        <Medal icon={Route} tone="glacier" size={36} />
        <div class="g-card-head-text">
          <div class="g-card-title">Pistes</div>
          <div class="g-sub">
            {pistes.length} piste{pistes.length > 1 ? 's' : ''} · {km(totalM)} · {open.length} ouverte{open.length > 1 ? 's' : ''} ({km(openM)})
          </div>
        </div>
        <button class="sc-ibtn sc-ibtn-sm sc-ibtn-ghost" aria-label="Fermer" onClick={() => (ui.pistesPanel.value = false)}>
          <X size={14} />
        </button>
      </div>
      <div class="sc-hud-body pst-body">
        <div class="kh-actions pst-top-actions">
          {owner && (
            <Button size="sm" onClick={startTrace}>
              <Plus size={14} /> Tracer une piste
            </Button>
          )}
          <Button size="sm" variant="secondary" onClick={() => (ui.trailMap.value = true)} title="Plan des pistes (L)">
            <MountainSnow size={14} /> Plan des pistes
          </Button>
        </div>
        <div class="g-section-title">
          <span>Où peut-on aller depuis le village ?</span>
        </div>
        {levels.map(({ lv, n, m, lifts }) => (
          <div class="pst-level" key={lv.skill}>
            <div class="pst-level-head">
              <b>{lv.label}</b>
              <span class="muted">{lv.hint}</span>
              <span class="num">
                {n} piste{n > 1 ? 's' : ''} · {km(m)}
              </span>
            </div>
            <span class="pst-meter" title={`${km(m)} sur ${km(openM)} ouverts, ${lifts} remontée${lifts > 1 ? 's' : ''}`}>
              <i style={{ width: `${openM > 0 ? Math.round((m / openM) * 100) : 0}%` }} />
            </span>
          </div>
        ))}
        <div class="g-section-title">
          <span>À corriger</span>
          <span>{issues.length || ''}</span>
        </div>
        {issues.length ? issues.map((i) => <IssueLine key={`${i.kind}:${i.pisteId ?? ''}:${i.liftId ?? ''}`} issue={i} onClick={() => focusIssue(i)} />) : <p class="g-empty">Aucun défaut : chaque piste part d'un sommet ou d'une piste et mène à une gare ou au village.</p>}
        <div class="g-section-title">
          <span>Toutes les pistes</span>
        </div>
        {pistes.map((p) => {
          const sel = ui.selection.value?.kind === 'piste' && ui.selection.value.id === p.id;
          return (
            <div class={`pst-row${sel ? ' on' : ''}`} key={p.id}>
              <button type="button" class="g-mini-row" onClick={() => focusEntity('piste', p.id)} title={pisteFacts(p)}>
                <span class="g-live-dot" style={{ background: DIFF_COLOR[p.difficulty] }} />
                <span class="g-live-name">
                  {p.name} <span class="muted">· {km(p.length)}</span>
                </span>
                <span class={`g-live-val${p.open ? '' : ' pst-closed'}`}>{stateText(p)}</span>
              </button>
              {owner && (
                <button type="button" class="sc-ibtn sc-ibtn-sm sc-ibtn-ghost" aria-label={`Modifier le tracé de ${p.name}`} title="Modifier le tracé" onClick={() => startEdit(p)}>
                  <Pencil size={13} />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
